import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageScenario,
  ProductImageText,
} from "./types";

/**
 * Surfaces from the 2026-10 surface campaign that need data the curator
 * tenant cannot hold: an organisation without Scheduler and without an
 * accepted data-processing agreement (PUB), an organisation running the free
 * analysis, a partner system's catalog submission, and a colleague's claim on
 * a help request. The capture fixtures in
 * apps/dashboard/e2e/product-images/fixtures/ prepare each of them through
 * normal product actions.
 */
type Draft = {
  id: string;
  module: string;
  opens: string;
  setup: readonly string[];
  scenario: ProductImageScenario;
  area: ProductImageArea;
  sv: ProductImageText;
  en: ProductImageText;
};

function entry(draft: Draft): ProductImageEntry {
  return {
    id: draft.id,
    module: draft.module,
    area: draft.area,
    opens: draft.opens,
    setup: draft.setup,
    scenario: draft.scenario,
    visual: { variant: 0 },
    text: { sv: draft.sv, en: draft.en },
    use: ["help"],
  };
}

const NEW_ORG = "Ny organisation utan Scheduler och utan godkänt PUB";
const FREE = "Gratis analys med uppladdade rapporter";

export const CAMPAIGN_SPECIAL_DATA_CATALOGUE: readonly ProductImageEntry[] = [
  // ── Ny organisation: PUB och Scheduler ────────────────────────────────
  entry({
    id: "settings.personal-data-processing",
    module: "Personuppgiftsbiträdesavtal",
    opens: "/account",
    setup: [NEW_ORG, "Öppna Konto"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Personuppgiftsbiträdesavtalet med avtalstext, kryssruta och knappen Godkänn PUB",
      shows:
        "Avtalet som organisationen läser och godkänner innan personuppgifter får läggas in.",
      proves:
        "Knappen Godkänn PUB går att använda först när hela avtalet är läst och rutan är ikryssad.",
    },
    en: {
      alt: "The data processing agreement with the agreement text, a checkbox and the Accept DPA button",
      shows:
        "The agreement the organisation reads and accepts before personal data may be added.",
      proves:
        "The Accept DPA button only works once the whole agreement has been read and the box is ticked.",
    },
  }),
  entry({
    id: "settings.personal-data-processing-required",
    module: "PUB krävs",
    opens: "/welcome",
    setup: [NEW_ORG, "Öppna välkomstsidan"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Meddelandet PUB krävs innan personuppgifter på välkomstsidan",
      shows:
        "Påminnelsen om att personuppgiftsbiträdesavtalet ska godkännas innan uppgifter om medarbetare och kunder läggs in.",
      proves:
        "Organisationen kan utforska produkten, men personuppgifter är låsta tills avtalet är godkänt.",
    },
    en: {
      alt: "The DPA required before personal data message on the welcome page",
      shows:
        "The reminder that the data processing agreement must be accepted before employee and client details are added.",
      proves:
        "The organisation can explore the product, but personal data stays locked until the agreement is accepted.",
    },
  }),
  entry({
    id: "settings.account.scheduler-purchase",
    module: "Scheduler är inte aktiv",
    opens: "/account",
    setup: [NEW_ORG, "Öppna Konto"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Meddelandet Scheduler är inte aktiv med länk till Produkter och Checkout",
      shows:
        "Kontosidan för en organisation som inte har Scheduler, med vägen till köpet.",
      proves:
        "Organisationens uppgifter finns kvar, och planeringen öppnas igen när Scheduler köps.",
    },
    en: {
      alt: "The Scheduler is not active message with a link to Products and Checkout",
      shows:
        "The account page for an organisation without Scheduler, with the way to buy it.",
      proves:
        "The organisation's data is kept, and planning opens again once Scheduler is bought.",
    },
  }),
  entry({
    id: "standing-data.client-form-dpa-required",
    module: "Ny kund: PUB krävs",
    opens: "/resources/clients/create",
    setup: ["Organisation med Scheduler men utan godkänt PUB", "Öppna Ny kund"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Formuläret för ny kund ersatt av personuppgiftsbiträdesavtalet",
      shows:
        "Att formuläret för en ny kund visar avtalet i stället för fälten så länge PUB inte är godkänt.",
      proves:
        "Inga uppgifter om en kund kan sparas innan organisationen har godkänt avtalet.",
    },
    en: {
      alt: "The new client form replaced by the data processing agreement",
      shows:
        "The new client form shows the agreement instead of its fields while the DPA is not accepted.",
      proves:
        "No client details can be saved before the organisation has accepted the agreement.",
    },
  }),
  entry({
    id: "import.upload-unavailable",
    module: "Uppladdning ej möjlig",
    opens: "/import",
    setup: [NEW_ORG, "Öppna Importera"],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Meddelandet Skapa en enhet innan du laddar upp besökshistorik",
      shows:
        "Importsidan när organisationen ännu inte har någon enhet att ladda upp till.",
      proves:
        "Sidan säger vad som saknas och leder direkt till att skapa enheten.",
    },
    en: {
      alt: "The Create a unit before uploading visit history message",
      shows:
        "The import page when the organisation has no unit to upload to yet.",
      proves:
        "The page says what is missing and leads straight to creating the unit.",
    },
  }),
  entry({
    id: "import.free-analysis",
    module: "Analysera ert schema",
    opens: "/import?intent=analysis",
    setup: [NEW_ORG, "Öppna gratis analys"],
    scenario: "S5",
    area: "schedule",
    sv: {
      alt: "Startsidan för gratis analys med vilka rapporter som behövs",
      shows:
        "Där en ny organisation börjar den kostnadsfria analysen av sitt eget schema.",
      proves:
        "Sidan visar vilka rapporter som krävs och vad analysen ger innan något laddas upp.",
    },
    en: {
      alt: "The free analysis start page with the reports that are needed",
      shows:
        "Where a new organisation starts the free analysis of its own schedule.",
      proves:
        "The page shows which reports are required and what the analysis gives before anything is uploaded.",
    },
  }),
  // ── Gratis analys med resultat ────────────────────────────────────────
  entry({
    id: "import.free-analysis-example",
    module: "Det här är exempeldata",
    opens: "/import?intent=analysis",
    setup: [FREE, "Ladda upp Caires exempelrapporter"],
    scenario: "S5",
    area: "schedule",
    sv: {
      alt: "Meddelandet Det här är exempeldata ovanför analysen",
      shows:
        "Att en analys gjord på Caires exempelrapporter säger det tydligt och kan ersättas med egna rapporter.",
      proves: "Exempeldata kan aldrig misstas för organisationens egen plan.",
    },
    en: {
      alt: "The This is example data message above the analysis",
      shows:
        "An analysis made from Caire's example reports says so clearly and can be replaced with your own reports.",
      proves:
        "Example data can never be mistaken for the organisation's own plan.",
    },
  }),
  entry({
    id: "import.free-analysis-campaign",
    module: "Er kostnadsfria optimering",
    opens: "/import?intent=analysis",
    setup: [FREE, "Välj Godkänn och optimera"],
    scenario: "S5",
    area: "solution",
    sv: {
      alt: "Panelen Er kostnadsfria optimering med status för förslagen",
      shows:
        "Hur långt den kostnadsfria optimeringen har kommit och vilka resultat som är klara.",
      proves:
        "Planerat, utfört och optimerat visas var för sig när underlaget finns.",
    },
    en: {
      alt: "The Your free optimization panel with the status of the proposals",
      shows:
        "How far the free optimization has come and which results are ready.",
      proves:
        "Planned, performed and optimized results are shown separately when the evidence is available.",
    },
  }),
  entry({
    id: "import.free-analysis-workspace",
    module: "Gratis analys: översikt",
    opens: "/import?intent=analysis&analysis_view=summary",
    setup: [FREE, "Öppna översikten"],
    scenario: "S5",
    area: "solution",
    sv: {
      alt: "Den gratis analysens översikt med resultat och vyer i sidomenyn",
      shows:
        "Analysens arbetsyta med översikt, jämförelse, analys, underlag och kalender.",
      proves:
        "Samma vyer som i plattformen visas för den uppladdade perioden, utan att något ändras.",
    },
    en: {
      alt: "The free analysis overview with results and views in the side menu",
      shows:
        "The analysis workspace with overview, comparison, analysis, source and calendar.",
      proves:
        "The same views as in the platform are shown for the uploaded period, without changing anything.",
    },
  }),
  entry({
    id: "import.free-analysis-report-help",
    module: "Hjälp med rapporterna",
    opens: "/import?intent=analysis",
    setup: [FREE, "Välj Hjälp med rapporterna"],
    scenario: "S5",
    area: "schedule",
    sv: {
      alt: "Panelen Vilka rapporter behövs? med obligatoriska och valfria rapporter",
      shows:
        "Vilka rapporter som behövs för analysen och vilka som är valfria.",
      proves:
        "Hjälpen öppnas i sidan, så att den som laddar upp inte tappar bort var hen är.",
    },
    en: {
      alt: "The Which reports do you need? panel with required and optional reports",
      shows: "Which reports the analysis needs and which are optional.",
      proves:
        "The help opens on the page, so the person uploading does not lose their place.",
    },
  }),
  entry({
    id: "import.free-analysis-plan-reality",
    module: "En plan som håller",
    opens: "/import?intent=analysis&analysis_view=plan_reality",
    setup: [FREE, "Öppna En plan som håller"],
    scenario: "S5",
    area: "solution",
    sv: {
      alt: "Rapporten En plan som håller för den uppladdade perioden",
      shows:
        "Rapporten som jämför organisationens plan med vad som utfördes och med Caires förslag.",
      proves:
        "Rapporten bygger på organisationens egna rapporter för samma period.",
    },
    en: {
      alt: "The A plan that holds report for the uploaded period",
      shows:
        "The report that compares the organisation's plan with what was performed and with Caire's proposal.",
      proves:
        "The report is built from the organisation's own reports for the same period.",
    },
  }),
  // ── CAIRE Connect ────────────────────────────────────────────────────
  entry({
    id: "import.connect-intake",
    module: "Inskickade underlag från CAIRE Connect",
    opens: "/import?serviceAreaId=…&partnerSubmissionId=…",
    setup: [
      "Ett verksamhetssystem skickar in underlag för enheten",
      "Öppna länken till underlaget",
    ],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Listan med inskickade underlag från CAIRE Connect och status Väntar på granskning",
      shows:
        "Underlag som ett anslutet verksamhetssystem har skickat in, med period, antal och när det löper ut.",
      proves:
        "Inget från partnern används förrän en planerare har granskat och godkänt underlaget.",
    },
    en: {
      alt: "The list of submissions from CAIRE Connect with the status Pending review",
      shows:
        "Submissions a connected care system has sent, with period, counts and when they expire.",
      proves:
        "Nothing from the partner is used until a planner has reviewed and accepted the submission.",
    },
  }),
  entry({
    id: "import.connect-intake-review",
    module: "Granska underlag",
    opens: "/import?serviceAreaId=…&partnerSubmissionId=…",
    setup: [
      "Öppna ett inskickat underlag från CAIRE Connect",
      "Välj Granska underlag",
    ],
    scenario: "S5",
    area: "standing-data",
    sv: {
      alt: "Granskningen av ett inskickat underlag med kunder, medarbetare och besök",
      shows:
        "Samma granskning som för en uppladdad fil, nu för underlaget från verksamhetssystemet.",
      proves:
        "Planeraren ser exakt vad som skulle läggas in innan något sparas.",
    },
    en: {
      alt: "The review of a submission with clients, employees and visits",
      shows:
        "The same review as for an uploaded file, here for the submission from the care system.",
      proves:
        "The planner sees exactly what would be added before anything is saved.",
    },
  }),
  // ── Hjälpförfrågan ───────────────────────────────────────────────────
  entry({
    id: "communication.hub.help-claim-review",
    module: "Övertag att granska",
    opens: "/communications?tab=help",
    setup: [
      "En medarbetare ber om hjälp med ett besök i appen",
      "En kollega som redan är upptagen vill ta över",
      "Öppna Kommunikation → Hjälp",
    ],
    scenario: "S3",
    area: "operations",
    sv: {
      alt: "Hjälpförfrågan där en kollega vill ta över besöket, med varningar och knappen Tilldela ändå",
      shows:
        "En kollegas begäran att ta över ett besök som väntar på planerarens beslut.",
      proves:
        "Ett övertag som krockar med kollegans eget schema genomförs inte av sig självt; planeraren ser varningarna och bestämmer.",
    },
    en: {
      alt: "A help request where a colleague wants to take over the visit, with warnings and the Assign anyway button",
      shows:
        "A colleague's request to take over a visit, waiting for the planner's decision.",
      proves:
        "A takeover that clashes with the colleague's own schedule is not applied by itself; the planner sees the warnings and decides.",
    },
  }),
];
